/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Grid,
  Stack,
  Typography,
  Card,
  CardContent,
  useMediaQuery,
  SvgIcon,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  getFormattedDateShortMonthDDYYYY,
  OutlinedIcons,
  CustomIcons,
  getPPPDString,
  getBudgetFlexibilityString,
  getTotalBudgetString,
} from '@marageti/z4-lib';
import { TripRequest } from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { TeamMember } from '@marageti/z4-sdk/lib/people';
import { titleCase } from '../../../../utils/helpers';
import { TravelerChips, TripRequestStatusChip } from '../../chips';
import {
  IPIndicator, PecvIndicator, PhoneIndicator, RecaptchaScoreIndicator,
} from '../../trip-indicators';

const formatCountryCodesToString = (countryCodes: string[], countryCodeMap: { [key: string]: string }) => {
  let countryNames: string[] = [];
  if (countryCodes) {
    countryNames = countryCodes.map((c) => countryCodeMap[c]);
  }
  return countryNames.join(', ');
};

const {
  OutlinedLocationMarkerIcon,
  OutlinedCurrencyDollarIcon,
  OutlinedClockIcon,
  OutlinedInboxInIcon,
} = OutlinedIcons;

const {
  DepartureIcon,
} = CustomIcons;

const IconLabel = ({ icon, label }: { icon: any, label: any }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <SvgIcon color="disabled" component={icon} />
    <Typography variant="caption">
      {label}
    </Typography>
  </Stack>
);

type IncomingCardViewProps = {
  countryCodes: CountryCodeMap | undefined;
  tripRequest: TripRequest;
  loggedInTeamMember: TeamMember | null;
  isAdmin: boolean;
};

const IncomingCardView = ({
  countryCodes = {},
  tripRequest,
  loggedInTeamMember,
  isAdmin,
}: IncomingCardViewProps) => {
  const {
    travelers,
    countries,
    departureDate: trDepartureDate,
    totalNights: trTotalNights,
    numberOfAdults,
    numberOfChildren,
    totalBudget,
    createdAt,
    referralType,
    repeatTraveler,
    recaptchaScore,
    pppd,
    budgetFlexibility,
    status,
    submittedFrom,
    initialPecv,
  } = tripRequest;
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('desktop'));
  const fullName = titleCase(travelers[0].fullName);
  const countryNames = countryCodes ? formatCountryCodesToString(countries, countryCodes) : '';
  const departureDate = getFormattedDateShortMonthDDYYYY(trDepartureDate) || '';
  const totalNights = `${trTotalNights} Nights`;
  const numberOfTravelers = numberOfAdults + numberOfChildren;
  const totalTravelers = numberOfTravelers === 1
    ? '1 Traveler'
    : `${numberOfTravelers} Travelers`;
  const submittedDate = getFormattedDateShortMonthDDYYYY(createdAt) || '';
  const timeZoneName = loggedInTeamMember?.timeZone?.name || '';
  const submittedTime = timeZoneName ? new Date(createdAt).toLocaleTimeString('en-US', {
    timeZone: timeZoneName.replace(' ', '_'),
    timeStyle: 'short',
  }) : '';
  return (
    <Card
      component={Link}
      sx={{
        '&:hover': {
          bgcolor: 'grey.A100',
        },
        textDecoration: 'none',
      }}
      to={`/trip-requests/${tripRequest?.id}`}
    >
      <CardContent>
        {isMobile ? (
          <Stack direction="column" spacing={2}>
            <Stack direction="row" spacing={1}>
              <Typography variant="bodyBold">
                {fullName}
              </Typography>
              <TravelerChips
                travelers={travelers}
                referralType={referralType}
                repeatTraveler={repeatTraveler}
              />
            </Stack>

            <Stack direction="column" spacing={1}>
              <IconLabel icon={OutlinedLocationMarkerIcon} label={countryNames} />
              <IconLabel
                icon={DepartureIcon}
                label={(
                  <>
                    {departureDate}
                    {' '}
                    ·
                    {' '}
                    {totalNights}
                  </>
                )}
              />
              <IconLabel
                icon={OutlinedCurrencyDollarIcon}
                label={(
                  <>
                    {getTotalBudgetString(totalBudget)}
                    {' '}
                    Total Budget
                    {' '}
                    ·
                    {' '}
                    {getPPPDString(pppd)}
                    {budgetFlexibility ? ` · ${getBudgetFlexibilityString(budgetFlexibility)}` : ''}
                  </>
                )}
              />
              <IconLabel
                icon={OutlinedInboxInIcon}
                label={`Requested: ${submittedDate}`}
              />
              <IconLabel icon={OutlinedClockIcon} label={submittedTime || '-'} />
            </Stack>

            <Grid
              container
              alignItems="center"
            >
              <Grid item xs={6} display="flex">
                <TripRequestStatusChip status={status} />
              </Grid>
              <Grid item xs={6} display="flex" justifyContent="flex-end">
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <PhoneIndicator
                    primaryTravelerPhone={travelers[0].phone}
                  />
                  <IPIndicator countryCodes={countryCodes} submittedFrom={submittedFrom} />
                  <Stack direction="column" alignItems="center" spacing={0.5}>
                    {isAdmin && <PecvIndicator initialPecv={initialPecv} tripRequest={tripRequest} />}
                    <RecaptchaScoreIndicator recaptchaScore={recaptchaScore} />
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        ) : (
          <Grid container direction="row" alignItems="center" columnSpacing={1.5}>
            <Grid item xs={3}>
              <Stack direction="row" spacing={1}>
                <Typography variant="bodyBold">
                  {fullName}
                </Typography>
                <TravelerChips
                  travelers={travelers}
                  referralType={referralType}
                  repeatTraveler={repeatTraveler}
                />
              </Stack>
              <Typography variant="caption">
                {countryNames}
              </Typography>
            </Grid>

            <Grid item xs={2}>
              <Typography variant="body">
                {departureDate}
              </Typography>
              <Typography variant="caption">
                {totalNights}
                {' '}
                ·
                {' '}
                {totalTravelers}
              </Typography>
            </Grid>

            <Grid item xs={2.5}>
              <Typography variant="body">
                {getPPPDString(pppd)}
              </Typography>
              <Typography variant="caption">
                {`${getTotalBudgetString(totalBudget)} Total Budget${budgetFlexibility ? ` · ${getBudgetFlexibilityString(budgetFlexibility)}` : ''}`}
              </Typography>
            </Grid>

            <Grid item xs={2}>
              <Typography variant="body">
                Requested:
                {' '}
                {submittedDate}
              </Typography>
              <Typography variant="caption">
                {submittedTime}
              </Typography>
            </Grid>

            <Grid item xs={1.5}>
              <Stack direction="row" justifyContent="center" spacing={1.5} alignItems="center">
                <PhoneIndicator
                  primaryTravelerPhone={travelers[0].phone}
                />
                <IPIndicator countryCodes={countryCodes} submittedFrom={submittedFrom} />
                <Stack direction="column" alignItems="center" spacing={0.5}>
                  {isAdmin && <PecvIndicator initialPecv={initialPecv} tripRequest={tripRequest} />}
                  <RecaptchaScoreIndicator recaptchaScore={recaptchaScore} />
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={1} display="flex" justifyContent="flex-end" alignItems="center">
              <TripRequestStatusChip status={status} />
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default IncomingCardView;
